import { useCallback, useEffect, useState } from 'react'
import { DateTime } from 'luxon'

import {
  permanentDstLabel,
  resolveDisplayZone,
  zoneAbbreviation,
} from '../ts-utils/permanent-dst'

/**
 * Reads an instant in a venue's zone, correcting for a province that abolished
 * its clock change before the viewer's timezone database caught up.
 *
 * Luxon resolves zone rules through `Intl`, i.e. the JS engine's own IANA copy,
 * and every shipped engine still carries British Columbia's and Alberta's old
 * fall-back — so a correctly stored November instant reads an hour early and
 * labelled PST. Unlike dayjs there is no singleton to patch, so each luxon
 * entry point is corrected on its own; this hook is the one the game rows,
 * event rows, game slots and the date picker all share.
 *
 * The substitute zone is right about the offset but not about its name, so the
 * label is carried alongside and put back when the value is formatted. See
 * ts-utils/permanent-dst for the rules and for when nothing needs doing.
 */
const inZone = (iso: string, timezone: string): DateTime => {
  const asInstant = DateTime.fromISO(iso)
  const displayZone = resolveDisplayZone(timezone, asInstant.toMillis())
  return asInstant.setZone(displayZone).setLocale('en-US')
}

interface Props {
  timezone?: string
  date?: string
  isTimeToBeDefined?: boolean
}

type TDate = DateTime<true> | DateTime<false> | null

interface HookReturn {
  userDate: TDate
  locationDate: TDate
  utcDate: TDate
  checkLocationDateAndUserDateAreSame: () => boolean
  getDateString: (date: TDate, format: string) => string
  getDateISOString: (date: TDate) => string
  DateTime: typeof DateTime
}

export const useTimezoneManager = ({
  date,
  timezone,
  isTimeToBeDefined = false,
}: Props): HookReturn => {
  const [userDate, setUserDate] = useState<TDate>(null)
  const [locationDate, setLocationDate] = useState<TDate>(null)
  const [utcDate, setUtcDate] = useState<TDate>(null)
  //  value -> the real zone it was asked for in, so a formatted value can be
  //  given back the label its own region uses.
  const [realZones, setRealZones] = useState<Map<DateTime, string>>(
    () => new Map()
  )

  const checkLocationDateAndUserDateAreSame = useCallback(
    () => userDate?.toISO() === locationDate?.toISO(),
    [locationDate, userDate]
  )

  const getDateString = useCallback(
    (dateToString: TDate, format: string) => {
      if (isTimeToBeDefined) {
        // Return only the date, and instead of the time 'DATE - TBD'
        return `${dateToString?.toFormat('MMM dd, yyyy')} - TBD`
      }

      const formatted = dateToString?.toFormat(format) ?? ''

      if (dateToString === null) {
        return formatted
      }

      //  When the zone was substituted for its offset, the formatted string
      //  carries the SUBSTITUTE's abbreviation — Phoenix's 'MST' for a BC
      //  game. Swap in the one people there actually use. A no-op unless the
      //  format asked for a zone name, since nothing else in a formatted date
      //  reads as one.
      //
      //  Keyed off THIS value, not its zone or the venue's: the same function
      //  formats `userDate`, which sits in the viewer's own zone, and keying
      //  off the venue would relabel a Toronto viewer's correct 'EST' as
      //  'PDT'. Zone names will not do either, since Alberta and the Northwest
      //  Territories share a substitute but not a label.
      const realZone = realZones.get(dateToString)
      if (realZone === undefined) {
        return formatted
      }

      const label = permanentDstLabel(realZone, dateToString.toMillis())
      if (label === null) {
        return formatted
      }

      const abbreviation = zoneAbbreviation(
        dateToString.zoneName ?? '',
        dateToString.toMillis()
      )

      return abbreviation === '' || abbreviation === label
        ? formatted
        : formatted.split(abbreviation).join(label)
    },
    [isTimeToBeDefined, realZones]
  )

  const getDateISOString = useCallback((dateToISO: TDate | string) => {
    if (typeof dateToISO === 'string') {
      const d = DateTime.fromISO(dateToISO)

      return d.toISO() || ''
    }

    return dateToISO?.toISO() || ''
  }, [])

  useEffect(() => {
    if (date && timezone) {
      //  The VIEWER's zone gets the same treatment as the venue's: someone
      //  reading this in British Columbia has the identical stale database,
      //  and their own local time is the line the row shows most prominently.
      const viewerZone = DateTime.local().zoneName
      const user =
        viewerZone === null
          ? DateTime.fromISO(date).setLocale('en-US')
          : inZone(date, viewerZone)
      const location = inZone(date, timezone)
      const utc = DateTime.fromISO(date).toUTC().setLocale('en-US')

      //  Every value, not only the substituted ones: the label applies on an
      //  up-to-date runtime too, which would otherwise print 'CST' for Alberta.
      const zones = new Map<DateTime, string>([[location, timezone]])
      if (viewerZone !== null) {
        zones.set(user, viewerZone)
      }

      setUserDate(user)
      setLocationDate(location)
      setUtcDate(utc)
      setRealZones(zones)
    }
  }, [date, timezone])

  return {
    checkLocationDateAndUserDateAreSame,
    userDate,
    locationDate,
    utcDate,
    getDateString,
    getDateISOString,
    DateTime,
  }
}
